import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { Input, Switch, Form, Select, DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/lib/date-picker';

import css from './UsersListHeader.module.less';
import useTranslate from '@hooks/useTranslate';
import componentContent from './UsersListHeader.content.json';
import users, { getList } from '@stores/users';
import localeWeightStore, { LOCALE, localeWeightMap, setLocaleWeight } from '@stores/localeWeight';
import { DATE_FORMAT } from '@config/format';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface UsersListProps {}

const UsersListHeader: FunctionComponent<UsersListProps> = () => {
  const { filters: currentFilterParams, preloaders } = useStore(users);
  const content = useTranslate(componentContent);

  const [isDisplayBlocked, setIsDisplayBlocked] = useState(false);
  const [searchValue, setSearchValue] = useState(currentFilterParams.filter || '');
  const [registrationIpValue, setRegistrationIpValue] = useState(
    currentFilterParams.registrationIp || '',
  );
  const [discordNicknameValue, setDiscordNicknameValue] = useState(
    currentFilterParams.discordNickname || '',
  );
  const [udidValue, setUdidValue] = useState(currentFilterParams.filterUdid || '');

  const localeName = useStore(localeWeightStore);

  useEffect(() => {
    setIsDisplayBlocked(
      currentFilterParams.enabled === undefined ? false : !currentFilterParams.enabled,
    );
  }, [currentFilterParams]);

  useEffect(() => {
    setSearchValue(currentFilterParams.filter || '');
    setRegistrationIpValue(currentFilterParams.registrationIp || '');
    setDiscordNicknameValue(currentFilterParams.discordNickname || '');
    setUdidValue(currentFilterParams.filterUdid || '');
  }, [
    currentFilterParams.filter,
    currentFilterParams.registrationIp,
    currentFilterParams.discordNickname,
    currentFilterParams.filterUdid,
  ]);

  const onSearch = useCallback(
    (filter: string) => {
      getList({
        filters: {
          ...currentFilterParams,
          filter,
        },
      });
    },
    [currentFilterParams],
  );

  const onSearchByRegistrationIp = useCallback(
    (registrationIp: string) => {
      getList({
        filters: {
          ...currentFilterParams,
          registrationIp: registrationIp || undefined,
        },
      });
    },
    [currentFilterParams],
  );

  const onSearchByUdid = useCallback(
    (udid: string) => {
      getList({
        filters: {
          ...currentFilterParams,
          filterUdid: udid || undefined,
        },
      });
    },
    [currentFilterParams],
  );

  const onSearchByDiscordNickname = useCallback(
    (discordNickname: string) => {
      getList({
        filters: {
          ...currentFilterParams,
          discordNickname: discordNickname.trim() || undefined,
        },
      });
    },
    [currentFilterParams],
  );

  const displayBlocked = useCallback(
    (value: boolean) => {
      setIsDisplayBlocked(value);
      getList({
        filters: {
          ...currentFilterParams,
          enabled: !value,
        },
      });
    },
    [currentFilterParams],
  );

  const onChangeDateRange = useCallback(
    (range: RangePickerProps['value']) => {
      const [dateStart, dateEnd] = range ?? [];
      let registeredFrom;
      let registeredTo;

      if (dateStart) {
        registeredFrom = dateStart.startOf('day').format();
      }

      if (dateEnd) {
        registeredTo = dateEnd.startOf('day').format();
      }

      getList({
        filters: {
          ...currentFilterParams,
          registeredFrom,
          registeredTo,
        },
      });
    },
    [currentFilterParams],
  );

  const onChangeLocale = useCallback((newLocale: LOCALE) => {
    setLocaleWeight(newLocale);
    getList();
  }, []);

  return (
    <div className={css.root}>
      <div className={css.selectorsContainer}>
        <Search
          className={css.searchInput}
          value={searchValue}
          allowClear
          onChange={(event) => setSearchValue(event.target.value)}
          onSearch={onSearch}
          placeholder={content.searchPlaceholder}
        />

        <Search
          className={css.registrationIpInput}
          value={registrationIpValue}
          allowClear
          onChange={(event) => setRegistrationIpValue(event.target.value)}
          onSearch={onSearchByRegistrationIp}
          placeholder={content.registrationIpPlaceholder}
        />

        <Search
          className={css.discordNicknameInput}
          value={discordNicknameValue}
          allowClear
          onChange={(event) => setDiscordNicknameValue(event.target.value)}
          onSearch={onSearchByDiscordNickname}
          placeholder={content.discordNicknamePlaceholder}
          aria-label={content.discordNicknamePlaceholder}
        />

        <Search
          className={css.udidInput}
          value={udidValue}
          allowClear
          onChange={(event) => setUdidValue(event.target.value)}
          onSearch={onSearchByUdid}
          placeholder={content.udidPlaceholder}
        />

        <RangePicker
          className={css.dateRangeSelector}
          format={DATE_FORMAT}
          allowEmpty={[true, true]}
          placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
          onChange={onChangeDateRange}
        />

        <Select
          className={css.localeSelector}
          defaultValue={localeName}
          onChange={onChangeLocale}
        >
          {localeWeightMap &&
            Object.entries(localeWeightMap).map(([key, value]) => {
              return (
                <Option
                  value={key}
                  key={key}
                >
                  {value.name}
                </Option>
              );
            })}
        </Select>
      </div>

      <Form.Item label={content.displayBlocked}>
        <Switch
          checked={isDisplayBlocked}
          onChange={displayBlocked}
          loading={!!preloaders.getBlockedList}
        />
      </Form.Item>
    </div>
  );
};

export default UsersListHeader;
